import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import {
  Alert,
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  Divider,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  KioskApiError,
  KioskNotPairedError,
  KioskRevokedError,
  ResolveResult,
  cancelSession,
  getActiveSession,
  heartbeat,
  resolvePlayer,
  startSession,
} from "../lib/api";
import { getPairedDevice } from "../lib/auth";
import { classifyScan, useBarcodeScanner, useHeartbeat } from "../lib/hardware";
import { showErrorMessage, showMessage } from "../hooks/messages";
import { CameraQrScanner } from "../components/CameraQrScanner";
import { PinPad } from "../components/pin-pad";
import { FLUID, KIOSK_CONTENT_MAX } from "../lib/responsive";

interface OrphanCancelState {
  activeSessionId: string;
  pin: string;
  cancelling: boolean;
  error: string | null;
  /**
   * The player the staffer was trying to scan when we discovered the
   * orphan belonged to someone else. We re-run the start flow for THIS
   * player after the orphan is cancelled, so the staffer doesn't have
   * to re-scan.
   */
  pendingResolve: ResolveResult;
}

/**
 * State for the per-order picker modal shown after a successful player
 * resolve and before starting the session. Operator chooses which of
 * the player's collectable orders to include in THIS pickup; orders
 * left unchecked stay pending for the next pickup.
 *
 * Selection is per-order (not per-line-item) because the server-side
 * KioskSession is scoped to whole orderIDs — filtering at line-item
 * granularity would create a client-only intent that scanItem doesn't
 * enforce, and a tab refresh would silently expand the session back
 * to all items in the selected orders.
 */
interface OrderPickState {
  resolve: ResolveResult;
  userId: string;
  /** Set of order IDs the operator has ticked. Defaults to all on open. */
  checked: Set<string>;
  starting: boolean;
}

/**
 * Idle screen. Listens for any HID scan; classifies as badge / QR / unknown
 * and dispatches the resolve flow.
 */
export default function IdlePage() {
  const router = useRouter();
  const [status, setStatus] = useState<"idle" | "resolving">("idle");
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [orphanCancel, setOrphanCancel] = useState<OrphanCancelState | null>(null);
  const [orderPick, setOrderPick] = useState<OrderPickState | null>(null);
  // Lock parallel scans so a fast double-trigger (e.g. scanner double-buffer
  // or someone tapping the camera capture twice) cannot fire two
  // startSession calls at the same time and create a 409 against itself.
  const scanInFlightRef = useRef(false);

  // On mount: redirect to /pair if not yet paired.
  useEffect(() => {
    if (!getPairedDevice()) {
      router.replace("/pair");
    }
  }, [router]);

  useHeartbeat(heartbeat, () => {
    showErrorMessage({
      title: "Kiosk revoked",
      message: "An administrator has disabled this kiosk. Re-pair to continue.",
      autoClose: false,
    });
    router.replace("/pair");
  });

  const startAndRoute = async (resolve: ResolveResult, userId: string) => {
    const sessionResult = await startSession({
      playerID: resolve.player.id,
      userID: userId,
      orderIDs: resolve.collectableOrders.map((o) => o.id),
    });
    stashBootstrap(sessionResult.session.id, {
      resolve,
      session: sessionResult.session,
    });
    router.push(`/session/${sessionResult.session.id}`);
  };

  const routeToExistingOrphan = (activeSessionId: string, resolve: ResolveResult) => {
    // Same player rescanned an orphan. We don't pre-stash the session view —
    // SessionPage will refetch via fetchSessionWithBackoff to load the real
    // scannedItems (the orphan may have items the previous attempt scanned).
    stashBootstrap(activeSessionId, { resolve });
    showMessage({
      color: "blue",
      title: "Resumed previous pickup",
      message: `${resolve.player.name} had an unfinished session — picking up where we left off.`,
      autoClose: 4000,
    });
    router.push(`/session/${activeSessionId}`);
  };

  const handleScan = async (raw: string) => {
    if (scanInFlightRef.current) return;
    scanInFlightRef.current = true;
    setResolveError(null);
    try {
      const shape = classifyScan(raw);
      if (shape.kind === "barcode") {
        // Likely a product barcode scanned at the idle screen — ignore it.
        // The staff probably grabbed an item before scanning the player.
        return;
      }
      setStatus("resolving");
      let result: ResolveResult;
      try {
        result = await resolvePlayer(
          shape.kind === "qr"
            ? { qrPayload: shape.qrPayload }
            : { badgeCode: shape.badgeCode }
        );
      } catch (err) {
        handleResolveError(err, setResolveError);
        return;
      }
      if (!result.userID) {
        setResolveError(
          "We couldn't find this player's user account. Ask an admin to check the player record."
        );
        return;
      }
      if (result.collectableOrders.length === 0) {
        showMessage({
          color: "yellow",
          title: `${result.player.name} has nothing to collect`,
          message: "No paid or processing orders awaiting pickup.",
          autoClose: 4000,
        });
        return;
      }

      // Open the per-order picker. Operator confirms (with all orders
      // ticked by default) — confirmOrderPick() runs startAndRoute with
      // the chosen subset. Even for one-order players we show the modal
      // so the operator gets a "you scanned the right player" beat
      // before items start scanning.
      setOrderPick({
        resolve: result,
        userId: result.userID,
        checked: new Set(result.collectableOrders.map((o) => o.id)),
        starting: false,
      });
    } finally {
      scanInFlightRef.current = false;
      setStatus("idle");
    }
  };

  const confirmOrderPick = async () => {
    if (!orderPick || orderPick.starting) return;
    const checkedOrders = orderPick.resolve.collectableOrders.filter((o) =>
      orderPick.checked.has(o.id)
    );
    if (checkedOrders.length === 0) return; // button is disabled too; defence-in-depth
    setOrderPick((p) => (p ? { ...p, starting: true } : p));
    const filteredResolve: ResolveResult = {
      ...orderPick.resolve,
      collectableOrders: checkedOrders,
    };
    try {
      await startAndRoute(filteredResolve, orderPick.userId);
      // startAndRoute navigates; the modal will unmount with the page.
    } catch (err) {
      if (
        err instanceof KioskApiError &&
        err.kioskCode === "SESSION_ACTIVE_KIOSK"
      ) {
        // Orphan exists — close the picker and route into the orphan
        // recovery flow. The operator's tick choices are discarded
        // because the orphan's `orderIDs` are server-locked; surface
        // that intent loss to the staffer so they don't think their
        // selection carried over.
        setOrderPick(null);
        showMessage({
          color: "yellow",
          title: "Unfinished pickup found",
          message:
            "This kiosk has an existing session — your order selection is being replaced with that session's scope.",
          autoClose: 5000,
        });
        await handleOrphanSession(
          filteredResolve,
          setResolveError,
          setOrphanCancel,
          routeToExistingOrphan
        );
        return;
      }
      setOrderPick((p) => (p ? { ...p, starting: false } : p));
      handleResolveError(err, setResolveError);
    }
  };

  const closeOrderPick = () => {
    if (orderPick?.starting) return; // don't dismiss mid-request
    setOrderPick(null);
    showMessage({
      color: "gray",
      title: "Cancelled",
      message: "Scan a player again to start a new pickup.",
      autoClose: 3000,
    });
  };

  const toggleOrderChecked = (orderId: string) => {
    setOrderPick((p) => {
      if (!p) return p;
      const next = new Set(p.checked);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return { ...p, checked: next };
    });
  };

  // Aggregate every modal that should suppress barcode scans so adding
  // a future modal doesn't require touching this gate.
  const someModalOpen = !!orphanCancel || !!orderPick;
  useBarcodeScanner(handleScan, status === "idle" && !someModalOpen);

  const closeOrphanModal = () => {
    // Dismissing the modal does NOT cancel the server-side orphan — let
    // the staffer know the next scan will re-trigger this prompt so they
    // don't think "Not now" magically fixed anything.
    setResolveError(
      "Previous pickup is still active. Scan again to retry, or open Kiosk admin to revoke."
    );
    setOrphanCancel(null);
  };

  const submitOrphanCancel = async () => {
    if (!orphanCancel || orphanCancel.pin.length < 4) return;
    // Hold the scan lock across the cancel-then-retry window. Without
    // this a fast second scan during the modal-closed-but-not-yet-routed
    // gap could fire its own startAndRoute and race the auto-retry.
    scanInFlightRef.current = true;
    setOrphanCancel({ ...orphanCancel, cancelling: true, error: null });
    try {
      await cancelSession(
        orphanCancel.activeSessionId,
        "Auto-cancel — different player resolved at kiosk",
        orphanCancel.pin
      );
      const resolve = orphanCancel.pendingResolve;
      setOrphanCancel(null);
      setStatus("resolving");
      try {
        if (!resolve.userID) throw new Error("missing user id");
        await startAndRoute(resolve, resolve.userID);
      } catch (err) {
        handleResolveError(err, setResolveError);
        setStatus("idle");
      }
    } catch (err) {
      if (err instanceof KioskApiError) {
        if (err.kioskCode === "STAFF_PIN_INVALID") {
          setOrphanCancel({
            ...orphanCancel,
            cancelling: false,
            pin: "",
            error: "Wrong PIN. Try again.",
          });
          return;
        }
        if (err.kioskCode === "STAFF_PIN_RATE_LIMITED") {
          setOrphanCancel({
            ...orphanCancel,
            cancelling: false,
            pin: "",
            error: "Too many wrong PINs. Wait a minute before trying again.",
          });
          return;
        }
      }
      // Non-KioskApiError or unmapped kioskCode — leave PIN visible so
      // the staffer can retry the same input on a transient blip.
      console.error("[kiosk] orphan-cancel failed", err);
      setOrphanCancel({
        ...orphanCancel,
        cancelling: false,
        error: err instanceof Error ? err.message : "Couldn't cancel.",
      });
    } finally {
      scanInFlightRef.current = false;
    }
  };

  return (
    <Center
      sx={{
        minHeight: "100vh",
        width: "100%",
        padding: "clamp(16px, 3vw, 32px) clamp(8px, 2vw, 24px)",
      }}
    >
      <Stack
        align="center"
        spacing="xl"
        sx={{ width: "100%", maxWidth: KIOSK_CONTENT_MAX, textAlign: "center" }}
      >
        <Title order={1} sx={{ fontSize: FLUID.displayTitle, lineHeight: 1.1 }}>
          Medicoach Kiosk
        </Title>
        {status === "resolving" ? (
          <Group spacing="md">
            <Loader size="md" />
            <Text size="lg">Resolving player…</Text>
          </Group>
        ) : (
          <>
            <Text size="xl" color="dimmed">
              Ready for the next player.
            </Text>
            <Text size="md" color="dimmed" mt="md">
              Show your phone&apos;s QR code — the scanner reads it from the screen.
              Or hand the scanner to a player with a printed badge.
            </Text>
            <Text size="sm" color="dimmed" mt={4}>
              If the scanner can&apos;t read the QR off the screen, tap below
              to use the tablet camera instead.
            </Text>
            <Group spacing="sm" mt="md">
              <Button
                variant="outline"
                onClick={() => {
                  setResolveError(null);
                  setCameraOpen(true);
                }}
              >
                Scan QR with camera
              </Button>
            </Group>
          </>
        )}
        {resolveError && (
          <Box
            p="md"
            sx={(t) => ({
              border: `1px solid ${t.colors.red[3]}`,
              borderRadius: t.radius.md,
              background: t.colors.red[0],
              color: t.colors.red[8],
              maxWidth: 600,
            })}
          >
            <Text weight={600}>Couldn&apos;t resolve player</Text>
            <Text size="sm" mt={4}>
              {resolveError}
            </Text>
          </Box>
        )}
        <Group spacing="sm" mt="xl">
          <Button variant="subtle" onClick={() => router.push("/admin")}>
            Kiosk admin
          </Button>
        </Group>
      </Stack>
      <CameraQrScanner
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onScan={(qrPayload) => {
          setCameraOpen(false);
          void handleScan(qrPayload);
        }}
      />
      <Modal
        opened={!!orphanCancel}
        onClose={closeOrphanModal}
        title="Unfinished pickup at this kiosk"
        size="md"
        closeOnClickOutside={!orphanCancel?.cancelling}
        withCloseButton={!orphanCancel?.cancelling}
      >
        {orphanCancel && (
          <Stack>
            {/*
              PII boundary: only the NEW player's name is rendered here
              (pendingResolve.player). The previous player's identity —
              available server-side via getActiveSession but deliberately
              not surfaced in the UI — must never appear in this copy. A
              future copy edit that pulls `orphanCancel.activeSessionId`'s
              player into this message would leak one player's pickup
              state to the next person at the kiosk.
            */}
            <Text size="sm">
              The kiosk has an unfinished pickup from a previous player. Enter
              your staff PIN to cancel it so {orphanCancel.pendingResolve.player.name}
              {" "}can collect their items. The cancellation is recorded in the
              audit log.
            </Text>
            <Divider />
            <PinPad
              value={orphanCancel.pin}
              onChange={(v) => setOrphanCancel({ ...orphanCancel, pin: v, error: null })}
              disabled={orphanCancel.cancelling}
            />
            {orphanCancel.error && <Alert color="red">{orphanCancel.error}</Alert>}
            <Group position="right">
              <Button variant="subtle" onClick={closeOrphanModal} disabled={orphanCancel.cancelling}>
                Not now
              </Button>
              <Button
                color="red"
                loading={orphanCancel.cancelling}
                disabled={orphanCancel.pin.length < 4}
                onClick={submitOrphanCancel}
              >
                Cancel previous &amp; continue
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
      <Modal
        opened={!!orderPick}
        onClose={closeOrderPick}
        title={
          orderPick
            ? `${orderPick.resolve.player.name} is collecting…`
            : ""
        }
        size="md"
        closeOnClickOutside={!orderPick?.starting}
        withCloseButton={!orderPick?.starting}
      >
        {orderPick && (
          <Stack>
            <Text size="sm" color="dimmed">
              Tick which orders the player is taking now. Unticked
              orders stay pending for next pickup.
            </Text>
            <Divider />
            <Stack spacing="xs">
              {orderPick.resolve.collectableOrders.map((order) => {
                const itemSummary = order.items
                  .map(
                    (it) =>
                      `${it.productName}${it.variantName ? ` (${it.variantName})` : ""} ×${it.quantity}`
                  )
                  .join(", ");
                return (
                  <Box
                    key={order.id}
                    p="sm"
                    sx={(t) => ({
                      border: `1px solid ${t.colors.gray[3]}`,
                      borderRadius: t.radius.sm,
                    })}
                  >
                    <Group position="apart" align="flex-start">
                      <Checkbox
                        checked={orderPick.checked.has(order.id)}
                        onChange={() => toggleOrderChecked(order.id)}
                        disabled={orderPick.starting}
                        label={
                          <Stack spacing={2}>
                            <Group spacing="xs">
                              <Text weight={600}>{order.orderNumber}</Text>
                              <Badge size="sm" color="gray" variant="light">
                                R{(order.total / 100).toFixed(2)}
                              </Badge>
                            </Group>
                            <Text size="xs" color="dimmed">
                              {itemSummary}
                            </Text>
                          </Stack>
                        }
                      />
                    </Group>
                  </Box>
                );
              })}
            </Stack>
            <Group position="right" mt="sm">
              <Button
                variant="subtle"
                onClick={closeOrderPick}
                disabled={orderPick.starting}
              >
                Not now
              </Button>
              <Button
                color="green"
                loading={orderPick.starting}
                disabled={orderPick.checked.size === 0}
                onClick={confirmOrderPick}
              >
                Start pickup
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Center>
  );
}

const stashBootstrap = (
  sessionId: string,
  payload: { resolve: ResolveResult; session?: unknown }
): void => {
  try {
    window.sessionStorage.setItem(
      `kiosk.session.${sessionId}`,
      JSON.stringify(payload)
    );
  } catch {
    // Quota error — session page will refetch with backoff to absorb the
    // DDB consistency window.
  }
};

/**
 * Recovery path after `startSession` returns 409 SESSION_ACTIVE_KIOSK.
 *
 * The server-side lookup carries playerID only (NEVER name/avatar/orders)
 * so a mismatch can be detected without leaking the previous player's
 * profile to the next staffer.
 *
 * - Active matches the player who just scanned → re-route silently;
 *   SessionPage will refetch the up-to-date scannedItems.
 * - Active belongs to a different player → open a staff-PIN-gated cancel
 *   modal; the original scan retries after the orphan is cleared.
 * - Lookup returns null (race lost even after server-side retry) → show
 *   a manual escape hatch, do not loop.
 */
const handleOrphanSession = async (
  resolve: ResolveResult,
  setError: (msg: string) => void,
  openOrphanModal: (s: OrphanCancelState) => void,
  resumeSame: (activeSessionId: string, resolve: ResolveResult) => void
): Promise<void> => {
  let lookup;
  try {
    lookup = await getActiveSession({ expecting: true });
  } catch (err) {
    // Log the underlying failure so a returning operator can correlate
    // the user-facing dead-end with whatever broke (5xx, 401, network).
    // Never silently swallow — kiosk debugging happens off-tablet and
    // server logs are the only signal we get.
    console.error("[kiosk] getActiveSession failed during orphan recovery", err);
    setError(
      "This kiosk already has an active session, but we couldn't look it up. Open Kiosk admin to revoke."
    );
    return;
  }
  if (!lookup.active) {
    setError(
      "This kiosk already has an active session. Try again in a few seconds, or open Kiosk admin if it persists."
    );
    return;
  }
  if (lookup.active.playerID === resolve.player.id) {
    resumeSame(lookup.active.id, resolve);
    return;
  }
  openOrphanModal({
    activeSessionId: lookup.active.id,
    pin: "",
    cancelling: false,
    error: null,
    pendingResolve: resolve,
  });
};

const handleResolveError = (
  err: unknown,
  setError: (msg: string) => void
): void => {
  if (err instanceof KioskNotPairedError) {
    window.location.href = "/pair";
    return;
  }
  if (err instanceof KioskRevokedError) {
    window.location.href = "/pair";
    return;
  }
  if (err instanceof KioskApiError) {
    const codeMap: Record<string, string> = {
      QR_INVALID: "QR code is invalid. Try again or use a printed badge.",
      QR_CLOCK_SKEW:
        "The player's phone clock looks off. Ask them to enable auto-time, or use the printed badge.",
      QR_REPLAY:
        "This QR was already used a moment ago. Wait 30 seconds for the next code.",
      BADGE_NOT_FOUND:
        "Badge isn't registered. Has it been revoked? Ask an admin to issue a new one.",
      BADGE_FALLBACK_DISABLED:
        "Printed badges are turned off at this site. Ask the player to show their QR code from the Medicoach app, or tap 'Scan QR with camera'.",
      IDENTITY_REVOKED:
        "This player's badge has been revoked. They need a new one before they can collect.",
      WRONG_INSTITUTION:
        "This player belongs to a different institution.",
      EMAIL_AMBIGUOUS:
        "Multiple user accounts share this player's email. Contact support.",
      SESSION_ACTIVE_KIOSK:
        "This kiosk already has an active session. Complete or cancel it first.",
      SESSION_ACTIVE_PLAYER:
        "This player is already in a session at another kiosk.",
    };
    setError(
      err.kioskCode && codeMap[err.kioskCode]
        ? codeMap[err.kioskCode]
        : err.message
    );
    return;
  }
  setError("Something went wrong. Try again.");
};
